import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, ShieldCheck, ChevronDown } from "lucide-react";
import { useEffectiveRole } from "@/hooks/useEffectiveRole";
import { useDashboard } from "@/hooks/useDashboard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const STAFF_ROLES = [
  "ceo", "coo", "cto", "cfo", "cgo", "cmo", "cso",
  "account_manager", "auditor", "staff_member",
];
const CLIENT_ROLES = ["client_owner", "client_admin", "client_member", "client_viewer"];

/**
 * Header pill that shows the active role + impersonation banner if active.
 * Clicking opens the role-switch dialog. Per ADR-20260427-role-based-view-overlay.
 */
export default function RoleSwitcher() {
  const { t } = useTranslation();
  const { role, actualRole, scope, isImpersonating, setImpersonation, clearImpersonation } =
    useEffectiveRole();
  const { data } = useDashboard();
  const [open, setOpen] = useState(false);
  const [pendingRole, setPendingRole] = useState<string>(role);
  const [pendingTenant, setPendingTenant] = useState<string>(scope || "");

  const tenants = data?.clients ?? [];

  const apply = () => {
    if (pendingRole === actualRole && (!pendingTenant || pendingTenant === "_global")) {
      clearImpersonation();
    } else {
      setImpersonation({ role: pendingRole, tenant: pendingTenant || null });
    }
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setPendingRole(role);
          setPendingTenant(scope === "_global" ? "" : scope);
          setOpen(true);
        }}
        title={t("role_switcher.title")}
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[11px] transition-colors
          ${
            isImpersonating
              ? "border-amber-500/60 bg-amber-950/40 text-amber-300 hover:bg-amber-950/60"
              : "border-slate-700 bg-slate-900 text-slate-300 hover:text-slate-100 hover:border-slate-600"
          }`}
      >
        {isImpersonating ? <Eye className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
        <span className="font-mono uppercase">{role}</span>
        {isImpersonating && scope && scope !== "_global" && (
          <span className="text-[10px] opacity-70">· {scope}</span>
        )}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("role_switcher.dialog_title")}</DialogTitle>
            <DialogDescription>
              {t("role_switcher.dialog_subtitle", { actual: actualRole })}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            {/* Current state */}
            <div className="text-xs text-slate-400 space-y-1">
              <div>
                <span className="text-slate-500">{t("role_switcher.actual")}:</span>{" "}
                <Badge variant="default" className="!text-[10px] font-mono">
                  {actualRole}
                </Badge>
              </div>
              <div>
                <span className="text-slate-500">{t("role_switcher.current")}:</span>{" "}
                <Badge
                  variant={isImpersonating ? "amber" : "accent"}
                  className="!text-[10px] font-mono"
                >
                  {role}
                </Badge>
                {scope && scope !== "_global" && (
                  <span className="ml-1.5 text-slate-500">@ {scope}</span>
                )}
                {isImpersonating && (
                  <Badge variant="amber" className="!text-[10px] ml-2">
                    {t("role_switcher.impersonating")}
                  </Badge>
                )}
              </div>
            </div>

            {/* Role select */}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 block">
                {t("role_switcher.pick_role")}
              </label>
              <select
                value={pendingRole}
                onChange={(e) => setPendingRole(e.target.value)}
                className="w-full h-9 px-3 text-sm rounded-md border border-slate-700 bg-slate-900 text-slate-100"
              >
                <optgroup label={t("role_switcher.staff_roles")}>
                  {STAFF_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </optgroup>
                <optgroup label={t("role_switcher.client_roles")}>
                  {CLIENT_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>

            {/* Tenant select (only for client roles or staff with assigned scope) */}
            {(pendingRole.startsWith("client_") || pendingRole === "account_manager") && (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 block">
                  {t("role_switcher.pick_tenant")}
                </label>
                <select
                  value={pendingTenant}
                  onChange={(e) => setPendingTenant(e.target.value)}
                  className="w-full h-9 px-3 text-sm rounded-md border border-slate-700 bg-slate-900 text-slate-100"
                >
                  <option value="">— select tenant —</option>
                  {tenants.map((c) => (
                    <option key={c.slug} value={c.slug}>
                      {c.display_name} ({c.slug})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <p className="text-[11px] text-slate-500 mt-2">
              {t("role_switcher.help")}
            </p>
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" size="sm">
                {t("common.cancel")}
              </Button>
            </DialogClose>
            {isImpersonating && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  clearImpersonation();
                  setOpen(false);
                }}
              >
                <EyeOff className="h-3 w-3 mr-1" />
                {t("role_switcher.clear")}
              </Button>
            )}
            <Button variant="default" size="sm" onClick={apply}>
              {t("role_switcher.apply")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Floating banner when impersonating */}
      {isImpersonating && <ImpersonationBanner role={role} scope={scope} onClear={clearImpersonation} />}
    </>
  );
}

function ImpersonationBanner({
  role,
  scope,
  onClear,
}: {
  role: string;
  scope: string;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="fixed bottom-4 left-4 z-50 flex items-center gap-2 rounded-md border border-amber-500/60 bg-amber-950/90 backdrop-blur px-3 py-2 text-xs text-amber-200 shadow-2xl">
      <Eye className="h-3.5 w-3.5" />
      <span>
        {t("role_switcher.banner", { role, scope: scope || "_global" })}
      </span>
      <button
        type="button"
        onClick={onClear}
        className="ml-2 px-2 py-0.5 rounded border border-amber-500/40 bg-amber-900/40 hover:bg-amber-900/70 text-[10px] uppercase tracking-wider"
      >
        {t("role_switcher.clear_short")}
      </button>
    </div>
  );
}
